import {flatten, get, isEmpty, isNil, sortBy, orderBy, transform, unionBy, uniq} from 'lodash'
import theme from 'theme/index'
import {iconNames} from 'components/icon'
import uuid from 'helpers/uuid'
import {getCompanyOption, getContactOption} from 'helpers/data'
import {getSearchTerm, isStringMatching} from 'helpers/string'
import {fromNow, getDaysDueLabel, getDaysToDue, roundDaysDown} from 'helpers/dates'
import {getActivityIconNameForType} from 'helpers/activities'
import {userHasPermission} from 'helpers/user'
import {permissions} from 'helpers/constants'
import {getDateFilters} from 'helpers/dateRange'
import {filterDormantContacts, getContactName} from 'helpers/contacts'
import {getPercentageFor} from 'helpers/financial'
import {orderHasBeenQuoted} from 'helpers/quotes'
import {isOrderReasonMandatory} from 'helpers/orders'
import {
  findTeamMember,
  getAllTeamsForTeamId,
  getContactsForTeamOrUserOption,
  getTeamMembersForTeams,
  getTeamOrUserOption,
  getTeamsAndUsersOptions, UnallocatedTeamOption,
  WHOLE_COMPANY_TEAM_ID,
} from 'helpers/teams'

export const initialOpportunityStage = 0
export const finalOpportunityStage = -1

const MIN_NEXT_ACTIVITY_ROTTING_TIME = 24 * 60 * 60 * 1000
const MIN_CREATED_ROTTING_TIME = 48 * 60 * 60 * 1000

export const nullOpportunity = {
  id: -1,
  accountId: null,
  ref: null,
  desc: '',
  creatorId: null,
  ownerId: null,
  stageId: null,
  contactId: null,
  companyId: null,
  createdDate: null,
  expectedDate: null,
  requiredDate: null,
  value: 0,
  probability: 0,
  notes: null,
  closed: false,
  modifiedDate: null,
  syncId: null,
  guid: uuid(),
  summaryInfo: {
    totalActivities: 0,
    totalQuotedValue: 0,
    totalConfirmedValue: 0,
    lastActivity: null,
  },
  company: null,
  contact: null,
  salesman: null,
  history: null,
}

export const getNewOpportunity = (user, owner, opportunityStages, selectedCompany, selectedContact) => {
  const entity = {
    contact: selectedContact,
    company: selectedCompany,
  }

  return {
    ...nullOpportunity,
    companyId: get(selectedCompany, 'id', null),
    company: getCompanyOption(entity),
    contactId: get(selectedContact, 'id', null),
    contact: getContactOption(entity),
    accountId: get(user, 'id', null),
    creatorId: get(user, 'contactId', null),
    ownerId: get(owner, 'id', null),
    salesman: owner,
    stageId: getDefaultOpportunityStageId(opportunityStages),
    status: OpportunityStatus.open,
  }
}

export const getDefaultOpportunityStageId = (opportunityStages = []) => {
  return isEmpty(opportunityStages)
    ? null
    : get(opportunityStages[0], 'id', null)
}

export const getEditOpportunityStageOptions = (opportunityStages) => {
  return getOpportunityStageOptions(opportunityStages).map(option => {
    return {
      ...option,
      isDisabled: option.id === finalOpportunityStage,
    }
  })
}

export const getOpportunityStageOptions = (opportunityStages) => {
  return isEmpty(opportunityStages)
    ? []
    : opportunityStages.map(opportunityStage => {

      const id = get(opportunityStage, 'id', null)
      const name = get(opportunityStage, 'name', null)

      return {
        id,
        key: id,
        value: id,
        label: name,
        opportunityStage,
      }
    })
}

export const OpportunityStatus = {
  open: 'OPEN',
  won: 'WON',
  rejected: 'REJECTED',
}

export const getOpportunityStatusNameForOpportunity = (opportunity) => {
  return getOpportunityStatusName(get(opportunity, 'status', OpportunityStatus.open))
}

export const getOpportunityStatusName = (status) => {
  switch (status) {
    default:
    case OpportunityStatus.open:
      return 'Open'
    case OpportunityStatus.won:
      return 'Won'
    case OpportunityStatus.rejected:
      return 'Rejected'
  }
}

export const getOpportunityStatusOptions = () => {
  return [
    {
      id: OpportunityStatus.open,
      key: OpportunityStatus.open,
      value: OpportunityStatus.open,
      label: getOpportunityStatusName(OpportunityStatus.open),
    },
    {
      id: OpportunityStatus.won,
      key: OpportunityStatus.won,
      value: OpportunityStatus.won,
      label: getOpportunityStatusName(OpportunityStatus.won),
    },
    {
      id: OpportunityStatus.rejected,
      key: OpportunityStatus.rejected,
      value: OpportunityStatus.rejected,
      label: getOpportunityStatusName(OpportunityStatus.rejected),
    },
  ]
}

export const getEditOpportunityStatusOptions = () => {
  return getOpportunityStatusOptions().map(option => {
    return {
      ...option,
      colour: getColorForOpportunityStatus(option.id),
    }
  })
}

export const getColorForOpportunity = (opportunity) => {
  return getColorForOpportunityStatus(get(opportunity, 'status', null))
}

export const getColorForOpportunityStatus = (status) => {
  switch (status) {
    default:
    case OpportunityStatus.open:
      return theme.palette.opportunityStatus.open
    case OpportunityStatus.won:
      return theme.palette.opportunityStatus.won
    case OpportunityStatus.rejected:
      return theme.palette.opportunityStatus.rejected
  }
}

export const getOrderType = (opportunity, orderTypes) => {
  const orderTypeId = get(opportunity, 'orderTypeId', null)
  return orderTypes.find(orderType => orderType.id === orderTypeId)
}

export const OpportunityDateTypes = [
  {value: 'expected', label: 'Expected On'},
  {value: 'required', label: 'Required On'},
  {value: 'created', label: 'Created On'},
  {value: 'modified', label: 'Modified On'},
  {value: 'nextActivity', label: 'Next Activity On'},
  {value: 'lastActivity', label: 'Last Activity On'},
  {value: 'won', label: 'Won'},
  {value: 'rejected', label: 'Rejected'},
]

export const UnallocatedOption = {
  value: -1,
  label: 'Unallocated',
}

export const getOpportunityFilterParams = (
  searchTerm,
  stageId,
  status,
  orderReasons,
  rejectReasons,
  companies,
  users,
  jobTypes,
  dateTypes,
  targetStart,
  targetEnd) => {

  const search = getSearchTerm(searchTerm)

  const params = {}

  if (!isEmpty(search))
    params.searchTerm = search

  if (!isNil(stageId))
    params.stageId = [stageId]

  if (!isNil(status))
    params.status = [status]

  if (!isEmpty(orderReasons))
    params.orderReasonId = orderReasons

  if (!isEmpty(rejectReasons))
    params.rejectReasonId = rejectReasons

  if (!isEmpty(companies))
    params.companyId = companies.map(company => company.value)

  if (!isEmpty(users)) {
    const unallocatedOption = users.find(option => option === UnallocatedOption.value)
    const isUnallocatedSelected = !isNil(unallocatedOption)
    if (isUnallocatedSelected) {
      params.showUnallocated = true
    }

    params.ownerId = isUnallocatedSelected
      ? users.filter(option => option !== UnallocatedOption.value)
      : users
  }

  updateParamsForOrderType(params, jobTypes)

  if (targetStart && targetEnd) {
    dateTypes.forEach(dateType => {
      params[`${dateType}Start`] = targetStart
      params[`${dateType}End`] = targetEnd
    })
  }

  params.summary = true
  return params
}

export const updateParamsForOrderType = (params, jobTypes) => {
  if (isEmpty(jobTypes))
    return

  const jobTypeOptions = jobTypes
    .filter(jobType => !isNil(jobType) && jobType !== UnallocatedOption.value)

  if (!isEmpty(jobTypeOptions))
    params.orderTypeId = jobTypeOptions

  if (jobTypes.some(jobType => jobType === UnallocatedOption.value))
    params.showUnallocatedOrderType = true
}

export const getUnallocatedOpportunities = (opportunities) => {
  return opportunities.filter(opportunityIsUnallocated)
}

export const JobCardDeliveredStatusOption = {
  value: 5,
  label: 'Delivered',
}

export const getAllocatedOpportunities = (opportunities, ownerId) => {
  return opportunities.filter(opportunity => opportunityIsAllocated(opportunity, ownerId))
}

export const opportunityIsUnallocated = (opportunity) => {
  return isNil(opportunity.ownerId)
}

export const opportunityIsAllocated = (opportunity, ownerId) => {
  return opportunity.ownerId === ownerId
}

export const opportunityIsRejected = (opportunity) => {
  return opportunity.status === OpportunityStatus.rejected
}

export const opportunityIsWon = (opportunity) => {
  return opportunity.status === OpportunityStatus.won
}

export const opportunityIsOpen = (opportunity) => {
  return opportunity.status === OpportunityStatus.open
}

export const opportunityIsAtInitialStage = (opportunity) => {
  return opportunity.stageId === initialOpportunityStage
}

export const opportunityIsAtFinalStage = (opportunity) => {
  return opportunity.stageId === finalOpportunityStage
}

export const opportunityIsNextActivityRotting = (opportunity, baseDate = new Date()) => {
  const nextActivityDate = get(opportunity, 'nextActivity.startDate')
  return isNil(nextActivityDate) || ((baseDate.getTime() - nextActivityDate) > MIN_NEXT_ACTIVITY_ROTTING_TIME)
}

export const opportunityIsCreatedRotting = (opportunity, baseDate = new Date()) => {
  const createdDate = get(opportunity, 'createdDate')
  const createdDay = roundDaysDown(new Date(createdDate)).getTime()
  const baseDay = roundDaysDown(baseDate).getTime()
  const days = baseDay - createdDay
  return days >= MIN_CREATED_ROTTING_TIME
}

export const getNewActivityDataForOpportunity = (opportunity) => {
  return {
    opportunity,
    company: opportunity.company,
    contact: opportunity.contact,
  }
}

export const getOpportunityNextActivityWarning = (
  opportunity,
  highlightNoDate = true,
  now = new Date().getTime()
) => {
  let date = get(opportunity, 'nextActivity.startDate', null)

  if (isNil(date)) {
    return highlightNoDate
      ? {label: 'There is no activity scheduled', level: WARNING_LEVEL.ERROR}
      : null
  }

  if (date < now)
    return {label: 'Next activity is overdue', level: WARNING_LEVEL.ERROR}

  date = roundDaysDown(new Date(date)).getTime()
  const today = roundDaysDown(new Date(now)).getTime()

  return (date === today)
    ? {
      label: getDaysDueLabel(
        getDaysToDue(today, date),
        'due',
        'Next activity'),
      level: WARNING_LEVEL.WARNING,
    }
    : null
}

export const getOpportunityCreatedWarning = (opportunity, now = new Date().getTime()) => {
  const createdDate = get(opportunity, 'createdDate', null)

  const date = roundDaysDown(new Date(createdDate)).getTime()
  const today = roundDaysDown(new Date(now)).getTime()

  return (date === today)
    ? {
      label: 'Created today',
      level: WARNING_LEVEL.WARNING,
    }
    : {
      label: `Created ${fromNow(createdDate, now)}`,
      level: WARNING_LEVEL.ERROR,
    }
}

export const WARNING_LEVEL = {
  WARNING: 'WARNING',
  ERROR: 'ERROR',
}

export const getNextActivityIconColour = (nextActivityWarning) => {
  return isNil(nextActivityWarning)
    ? theme.palette.primaryFontColor
    : nextActivityWarning.level === WARNING_LEVEL.ERROR
      ? theme.palette.errorColor
      : theme.palette.warningColor
}

export const getNextActivityIconName = (nextActivity) => {
  return isNil(nextActivity)
    ? iconNames.warning
    : getActivityIconNameForType(get(nextActivity, 'activitytype.fixedType'))
}

export const updateOpportunities = (opportunities, updatedOpportunity) => {
  return opportunities.some(opportunity => opportunity.id === updatedOpportunity.id)
    ? opportunities.map(opportunity => opportunity.id === updatedOpportunity.id ? updatedOpportunity : opportunity)
    : [...opportunities, updatedOpportunity]
}

export const updateLeadsOpportunities = (leadsOpportunities, updatedOpportunity) => {
  leadsOpportunities[updatedOpportunity.ownerId] = leadsOpportunities[updatedOpportunity.ownerId || 0] || []

  return transform(leadsOpportunities, (opportunitiesObject, ownerOpportunities, key) => {
    const ownerId = parseInt(key)
    opportunitiesObject[ownerId] = sortLeadsOpportunityList(
      updateOpportunities(ownerOpportunities, updatedOpportunity)
        .filter(opportunityIsOpen)
        .filter(opportunityIsAtInitialStage)
        .filter(opportunity => (opportunity.ownerId || 0) === ownerId))
  }, {})
}

export const mergeLeadsOpportunities = (leadsOpportunities, updatedOpportunities) => {
  uniq(updatedOpportunities.map(opportunity => opportunity.ownerId)).forEach(ownerId => {
    leadsOpportunities[ownerId || 0] = leadsOpportunities[ownerId || 0] || []
  })

  return transform(leadsOpportunities, (opportunitiesObject, ownerOpportunities, key) => {
    const ownerId = parseInt(key)
    opportunitiesObject[ownerId] = sortLeadsOpportunityList(
      mergeOpportunities(ownerOpportunities, updatedOpportunities)
        .filter(opportunityIsOpen)
        .filter(opportunityIsAtInitialStage)
        .filter(opportunity => (opportunity.ownerId || 0) === ownerId))
  }, {})
}

export const removeFromLeadsOpportunities = (leadsOpportunities, opportunityId) => {
  return transform(leadsOpportunities, (opportunitiesObject, ownerOpportunities, key) => {
    const ownerId = parseInt(key)
    opportunitiesObject[ownerId] = ownerOpportunities.filter(opportunity => opportunity.id !== opportunityId)
  }, {})
}

export const mergeOpportunities = (opportunities, updatedOpportunities) => {
  return unionBy(updatedOpportunities, opportunities, 'id')
}

export const sortLeadsOpportunityList = (opportunities) => {
  return sortBy(opportunities, ['createdDate'])
}

export const opportunityHasNextActivity = (opportunity) => {
  return !isNil(opportunity.nextActivity)
}

export const opportunityHasNoNextActivity = (opportunity) => {
  return isNil(opportunity.nextActivity)
}

export const sortSellingOpportunityList = (opportunities) => {
  const opportunitiesWithoutNextActivity = opportunities.filter(opportunityHasNoNextActivity)
  const opportunitiesWithNextActivity = opportunities.filter(opportunityHasNextActivity)

  return [
    ...sortBy(opportunitiesWithoutNextActivity, ['createdDate']),
    ...sortBy(opportunitiesWithNextActivity, ['nextActivity.startDate', 'createdDate']),
  ]
}

export const getTotalOpportunityValue = (opportunities) => {
  return opportunities.reduce((total, opportunity) => total + opportunity.value, 0)
}

export const getLeadsParams = (ownerId, filters, jobTypes, search, invoiceFinancialYearStart, page, size) => {
  const {company, createdStart, createdEnd, createdTimeKey} = filters

  const dateFilters = {
    timeKey: createdTimeKey,
    targetStart: createdStart,
    targetEnd: createdEnd,
  }

  const {targetStart, targetEnd} = getDateFilters(dateFilters, invoiceFinancialYearStart)

  return {
    ownerId,
    showUnallocated: isNil(ownerId),
    stageId: initialOpportunityStage,
    status: [OpportunityStatus.open],
    companyId: isNil(company) ? null : company.value,
    orderTypeId: isEmpty(jobTypes) ? null : jobTypes,
    createdStart: targetStart,
    createdEnd: targetEnd,
    search: isEmpty(search) ? null : search,
    count: page === 0,
    value: page === 0,
    start: page * size,
    limit: size,
    sortColumn: ['createdDate'],
  }
}

export const canSaveOpportunity = (user, opportunity, orderTypeMandatory) => {
  const companyId = get(opportunity, 'companyId', null)
  const contactId = get(opportunity, 'contactId', null)
  const desc = get(opportunity, 'desc', null)
  const ownerId = get(opportunity, 'ownerId', null)
  const stageId = get(opportunity, 'stageId', null)
  const status = get(opportunity, 'status', null)
  const orderReasonId = get(opportunity, 'orderReasonId', null)
  const orderTypeId = get(opportunity, 'orderTypeId', null)
  const isSalesPersonRequired = isSalesPersonMandatory(opportunity)
    ? !isNil(ownerId)
    : true
  const statusIsOk = isWonStatusAllowed(opportunity) || !opportunityIsWon(opportunity)
  const isOrderTypeMandatory = orderTypeMandatory ? !isNil(orderTypeId) : true
  return (
    !isNil(companyId) &&
    !isNil(contactId) &&
    !isEmpty(desc) &&
    !isNil(stageId) &&
    !isEmpty(status) &&
    isOrderTypeMandatory &&
    (status !== OpportunityStatus.rejected || !isNil(orderReasonId)) &&
    (status !== OpportunityStatus.won || !isOrderReasonMandatory(user) || !isNil(orderReasonId)) &&
    isSalesPersonRequired &&
    statusIsOk
  )
}

export const getOpportunityRejectReason = (opportunity, orderReasons) => {
  return opportunity.status === OpportunityStatus.rejected && !isNil(opportunity.orderReasonId)
    ? orderReasons.find(reason => reason.id === opportunity.orderReasonId)
    : null
}

export const getOpportunityWonReason = (opportunity, orderReasons) => {
  return opportunity.status === OpportunityStatus.won && !isNil(opportunity.orderReasonId)
    ? orderReasons.find(reason => reason.id === opportunity.orderReasonId)
    : null
}

export const isSalesPersonMandatory = (opportunity) => {
  return !opportunityIsAtInitialStage(opportunity)
}

export const canEditOpportunity = (opportunity, user, homeContacts, teams, teamMembers) => {
  if (isNil(opportunity))
    return false

  if (opportunityIsUnallocated(opportunity))
    return userHasPermission(user, permissions.MANAGE_UNALLOCATED_OPPORTUNITIES)

  if (opportunity.ownerId === user.contactId ||
    userHasPermission(user, permissions.MANAGE_ALL_ALLOCATED_OPPORTUNITIES))
    return true

  const myTeamMember = findTeamMember(user.contactId, teamMembers)

  if (isNil(myTeamMember) || isNil(myTeamMember.managerTeamId))
    return false

  if (myTeamMember.managerTeamId === WHOLE_COMPANY_TEAM_ID)
    return true

  const allMyTeams = getAllTeamsForTeamId(myTeamMember.managerTeamId, teams)
  const allTeamMembers = getTeamMembersForTeams(allMyTeams, teamMembers)
  return allTeamMembers.some(teamMember => teamMember.contactId === opportunity.ownerId)
}

export const getCannotEditOpportunityReason = (opportunity, user, homeContacts, teams, teamMembers) => {
  if (isNil(opportunity))
    return null

  if (opportunityIsUnallocated(opportunity))
    return userHasPermission(user, permissions.MANAGE_UNALLOCATED_OPPORTUNITIES)
      ? null
      : 'You do not own this opportunity'

  if (opportunity.ownerId === user.contactId ||
    userHasPermission(user, permissions.MANAGE_ALL_ALLOCATED_OPPORTUNITIES))
    return null

  const myTeamMember = findTeamMember(user.contactId, teamMembers)

  if (isNil(myTeamMember) || isNil(myTeamMember.managerTeamId))
    return 'This opportunity is owned by another user'

  if (myTeamMember.managerTeamId === WHOLE_COMPANY_TEAM_ID)
    return null

  const allMyTeams = getAllTeamsForTeamId(myTeamMember.managerTeamId, teams)
  const allTeamMembers = getTeamMembersForTeams(allMyTeams, teamMembers)
  return allTeamMembers.some(teamMember => teamMember.contactId === opportunity.ownerId)
    ? null
    : 'This opportunity is owned by another team'
}

export const getSellingStages = (opportunityStages) => {
  const excludedStages = [
    finalOpportunityStage,
  ]

  return opportunityStages.filter(opportunityStage => !excludedStages.includes(opportunityStage.id))
}

export const getSellingParams = (
  stageId,
  filters,
  teamOrUserOption,
  search,
  teams,
  teamMembers,
  homeContacts,
  jobTypes,
  invoiceFinancialYearStart,
  showDormants,
  page,
  size) => {
  const {
    company, expectedStart, expectedEnd, expectedTimeKey,
    nextActivityStart, nextActivityEnd, nextActivityTimeKey,
  } = filters

  const expectedDateFilters = {
    timeKey: expectedTimeKey,
    targetStart: expectedStart,
    targetEnd: expectedEnd,
  }

  const nextActivityDateFilters = {
    timeKey: nextActivityTimeKey,
    targetStart: nextActivityStart,
    targetEnd: nextActivityEnd,
  }

  const expectedFilters = getDateFilters(expectedDateFilters, invoiceFinancialYearStart)
  const nextActivityFilters = getDateFilters(nextActivityDateFilters, invoiceFinancialYearStart)

  const ownerIds = getContactsForTeamOrUserOption(
    teamOrUserOption,
    teams,
    teamMembers,
    filterDormantContacts(homeContacts, showDormants)
  ).map(contact => contact.id)

  return {
    stageId,
    status: [OpportunityStatus.open],
    companyId: isNil(company) ? null : company.value,
    orderTypeId: isEmpty(jobTypes) ? null : jobTypes,
    expectedStart: expectedFilters.targetStart,
    expectedEnd: expectedFilters.targetEnd,
    ownerId: ownerIds,
    showUnallocated: teamOrUserOption === UnallocatedTeamOption.value,
    search: isEmpty(search) ? null : search,
    count: page === 0,
    value: page === 0,
    start: page * size,
    limit: size,
    summary: true,
    sortColumn: ['nextActivity', 'createdDate'],
    sortDirection: ['ASC', 'DESC'],
    nextActivityStart: nextActivityFilters.targetStart,
    nextActivityEnd: nextActivityFilters.targetEnd,
  }
}

export const updateSellingOpportunities = (sellingOpportunities, updatedOpportunity) => {
  sellingOpportunities[updatedOpportunity.stageId] = sellingOpportunities[updatedOpportunity.stageId] || []

  return transform(sellingOpportunities, (opportunitiesObject, stageOpportunities, key) => {
    const stageId = parseInt(key)
    opportunitiesObject[stageId] = sortSellingOpportunityList(
      updateOpportunities(stageOpportunities, updatedOpportunity)
        .filter(opportunityIsOpen)
        .filter(opportunity => opportunity.stageId === stageId))
  }, {})
}

export const removeFromSellingOpportunities = (sellingOpportunities, opportunityId) => {
  return transform(sellingOpportunities, (opportunitiesObject, stageOpportunities, key) => {
    const stageId = parseInt(key)
    opportunitiesObject[stageId] = stageOpportunities.filter(opportunity => opportunity.id !== opportunityId)
  }, {})
}

export const mergeSellingOpportunities = (sellingOpportunities, updatedOpportunities) => {
  uniq(updatedOpportunities.map(opportunity => opportunity.stageId)).forEach(stageId => {
    sellingOpportunities[stageId] = sellingOpportunities[stageId] || []
  })

  return transform(sellingOpportunities, (opportunitiesObject, stageOpportunities, key) => {
    const stageId = parseInt(key)
    opportunitiesObject[stageId] = sortSellingOpportunityList(
      mergeOpportunities(stageOpportunities, updatedOpportunities)
        .filter(opportunityIsOpen)
        .filter(opportunity => opportunity.stageId === stageId))
  }, {})
}

export const findSellingOpportunity = (sellingOpportunities, opportunityId) => {
  return flatten(Object.values(sellingOpportunities)).find(opportunity => opportunity.id === opportunityId)
}

export const findLeadsOpportunity = (leadOpportunities, opportunityId) => {
  return flatten(Object.values(leadOpportunities)).find(opportunity => opportunity.id === opportunityId)
}

export const findWonOpportunity = (wonOpportunities, opportunityId) => {
  return flatten(Object.values(wonOpportunities)).find(opportunity => opportunity.id === opportunityId)
}

export const updateLeadsCounts = (leadsCounts, previousOpportunity, updatedOpportunity) => {
  return updateCounts(
    leadsCounts,
    isNil(previousOpportunity) ? null : previousOpportunity.ownerId || 0,
    isNil(updatedOpportunity) ? null : updatedOpportunity.ownerId || 0)
}

export const updateLeadsValues = (leadsValues, previousOpportunity, updatedOpportunity) => {
  return updateValues(
    leadsValues,
    isNil(previousOpportunity) ? null : previousOpportunity.ownerId || 0,
    isNil(updatedOpportunity) ? null : updatedOpportunity.ownerId || 0,
    get(previousOpportunity, 'value'),
    get(updatedOpportunity, 'value'))
}

export const updateSellingCounts = (sellingCounts, previousOpportunity, updatedOpportunity) => {
  return updateCounts(
    sellingCounts,
    get(previousOpportunity, 'stageId'),
    get(updatedOpportunity, 'stageId'))
}

export const updateSellingValues = (sellingValues, previousOpportunity, updatedOpportunity) => {
  return updateValues(
    sellingValues,
    get(previousOpportunity, 'stageId'),
    get(updatedOpportunity, 'stageId'),
    get(previousOpportunity, 'value'),
    get(updatedOpportunity, 'value'))
}

export const updateCounts = (currentCounts, previousKey, updatedKey) => {
  if (isNil(previousKey) || isNil(updatedKey) || previousKey !== updatedKey) {
    const updatedCounts = {...currentCounts}

    if (!isNil(previousKey)) {
      const previousCount = currentCounts[previousKey] || 0
      if (previousCount > 0) updatedCounts[previousKey] = previousCount - 1
    }

    if (!isNil(updatedKey)) {
      const updatedCount = currentCounts[updatedKey] || 0
      updatedCounts[updatedKey] = updatedCount + 1
    }

    return updatedCounts
  }

  return currentCounts
}

export const updateValues = (currentValues, previousKey, updatedKey, previousValue, updatedValue) => {
  if (isNil(previousKey) || isNil(updatedKey) || previousKey !== updatedKey) {
    const updatedValues = {...currentValues}

    if (!isNil(previousKey)) {
      const previousKeyPreviousValue = currentValues[previousKey] || 0
      updatedValues[previousKey] = Math.max(0, previousKeyPreviousValue - previousValue)
    }

    if (!isNil(updatedKey)) {
      const updatedKeyPreviousValue = currentValues[updatedKey] || 0
      updatedValues[updatedKey] = updatedKeyPreviousValue + updatedValue
    }

    return updatedValues
  } else if (previousKey === updatedKey && previousValue !== updatedValue) {
    const currentValue = currentValues[previousKey] || 0
    return {
      ...currentValues,
      [previousKey]: Math.max(0, currentValue - previousValue) + updatedValue,
    }
  }

  return currentValues
}

export const fitsOwnerFilter = (
  user,
  teams,
  teamMembers,
  homeContacts,
  userOrTeam,
  showDormants,
  opportunity,
  showUnallocated = false
) => {
  if (showUnallocated && userOrTeam === UnallocatedTeamOption.value) {
    return isNil(opportunity.ownerId)
  }

  const teamUserOptions = getTeamsAndUsersOptions(user, teams, teamMembers, homeContacts, 'Leads', showUnallocated)
  const teamOrUserOption = getTeamOrUserOption(user, teams, teamMembers, teamUserOptions, userOrTeam)

  const ownerIds = getContactsForTeamOrUserOption(
    teamOrUserOption,
    teams,
    teamMembers,
    filterDormantContacts(homeContacts, showDormants)
  ).map(contact => contact.id)

  return ownerIds.includes(opportunity.ownerId)
}

export const fitsSellingStages = (opportunityStages, opportunity) => {
  const stages = getSellingStages(opportunityStages)
  return stages.map(stage => stage.id).includes(opportunity.stageId)
}

export const fitsDateFilter = (timeKey, start, end, invoiceFinancialYearStart, opportunity, key) => {
  const dateFilters = {
    timeKey: timeKey,
    targetStart: start,
    targetEnd: end,
  }

  const {targetStart, targetEnd} = getDateFilters(dateFilters, invoiceFinancialYearStart)
  const value = get(opportunity, key)

  return isNil(targetStart) || isNil(targetEnd) ||
    (!isNil(value) && value >= targetStart && value < targetEnd)
}

export const fitsOpportunitySearch = (search, opportunity) => {
  return isEmpty(search) ||
    isStringMatching(opportunity.ref, search) ||
    isStringMatching(opportunity.desc, search) ||
    isStringMatching(get(opportunity, 'company.name'), search) ||
    isStringMatching(getContactName(opportunity.contact), search)
}

export const opportunityFitsSellingFilters = (
  user,
  opportunity,
  filters,
  userOrTeam,
  search,
  opportunityStages,
  invoiceFinancialYearStart,
  showDormants,
  teams,
  teamMembers,
  homeContacts) => {

  const {
    jobTypes,
    company,
    expectedStart,
    expectedEnd,
    expectedTimeKey,
    nextActivityStart,
    nextActivityEnd,
    nextActivityTimeKey,
  } = filters

  const fitsOwner =
    fitsOwnerFilter(user, teams, teamMembers, homeContacts, userOrTeam, showDormants, opportunity, true)
  const fitsStage = fitsSellingStages(opportunityStages, opportunity)
  const fitsStatus = opportunity.status === OpportunityStatus.open
  const fitsCompany = isNil(company) || opportunity.companyId === company.value
  const fitsJobType = isEmpty(jobTypes) || jobTypes.includes(opportunity.orderTypeId)
  const fitsSearch = fitsOpportunitySearch(search, opportunity)

  const fitsActivity = fitsDateFilter(
    nextActivityTimeKey,
    nextActivityStart,
    nextActivityEnd,
    invoiceFinancialYearStart,
    opportunity,
    'nextActivity.startDate')

  const fitsExpected = fitsDateFilter(
    expectedTimeKey,
    expectedStart,
    expectedEnd,
    invoiceFinancialYearStart,
    opportunity,
    'expectedDate')

  return [
    fitsStage,
    fitsStatus,
    fitsCompany,
    fitsJobType,
    fitsOwner,
    fitsExpected,
    fitsActivity,
    fitsSearch,
  ].every(value => value === true)
}

export const isOpportunityStageSortDisabled = (opportunityStage) => {
  const id = get(opportunityStage, 'id', initialOpportunityStage)
  return id === initialOpportunityStage || id === finalOpportunityStage
}

export const getSuccessStatsParams = (
  filters,
  teamOrUserOption,
  teams,
  teamMembers,
  homeContacts,
  jobTypes,
  invoiceFinancialYearStart) => {

  const {stageModifiedStart, stageModifiedEnd, stageModifiedTimeKey} = filters
  const dateFilters = {
    timeKey: stageModifiedTimeKey,
    targetStart: stageModifiedStart,
    targetEnd: stageModifiedEnd,
  }

  const {targetStart, targetEnd} = getDateFilters(dateFilters, invoiceFinancialYearStart)

  const ownerIds = getContactsForTeamOrUserOption(
    teamOrUserOption,
    teams,
    teamMembers,
    homeContacts).map(contact => contact.id)

  const params = {
    stageModifiedStart: targetStart,
    stageModifiedEnd: targetEnd,
    ownerId: ownerIds,
  }

  if (!isEmpty(jobTypes))
    params.orderTypeId = jobTypes

  return params
}

export const findOpportunitySuccessStat = (opportunitySuccessStats, opportunityStageId) => {
  return opportunitySuccessStats.find(stat => stat.stageId === opportunityStageId)
}

export const getOpportunitySuccessStat = (opportunitySuccessStats, opportunityStages, opportunityStageId) => {
  if (isNil(opportunitySuccessStats)) return null

  const opportunityStat = findOpportunitySuccessStat(opportunitySuccessStats, opportunityStageId)
  const thisCount = get(opportunityStat, 'count', 0)
  const thisValue = get(opportunityStat, 'value', 0)

  const index = opportunityStages.findIndex(opportunityStage => opportunityStage.id === opportunityStageId)
  const nextStages = opportunityStages.filter((stage, stageIndex) => stageIndex > index)
  const nextStats = nextStages
    .map(stage => findOpportunitySuccessStat(opportunitySuccessStats, stage.id))
    .filter(stats => !isNil(stats))

  const nextCount = nextStats.reduce((total, stats) => total + stats.count, 0)
  const nextValue = nextStats.reduce((total, stats) => total + stats.value, 0)

  return {
    count: thisCount + nextCount,
    value: thisValue + nextValue,
  }
}

export const getPreviousOpportunitySuccessStat = (
  opportunitySuccessStats,
  opportunityStages,
  opportunityStageId
) => {
  const index = opportunityStages.findIndex(opportunityStage => opportunityStage.id === opportunityStageId)
  if (index > 0) {
    const previousStage = opportunityStages[index - 1]
    return getOpportunitySuccessStat(opportunitySuccessStats, opportunityStages, previousStage.id)
  }
  return null
}

export const getOpportunitySuccessConversion = (opportunitySuccessStat, previousOpportunitySuccessStat) => {
  return isNil(opportunitySuccessStat) || isNil(previousOpportunitySuccessStat)
    ? 0
    : getPercentageFor(opportunitySuccessStat.count, previousOpportunitySuccessStat.count)
}

export const fitsLeadsStage = (opportunity) => {
  return opportunity.stageId === initialOpportunityStage
}

export const opportunityFitsLeadsFilters = (
  user,
  opportunity,
  filters,
  search,
  invoiceFinancialYearStart,
  showDormants,
  teams,
  teamMembers,
  homeContacts) => {
  const {userOrTeam, jobTypes, company, createdStart, createdEnd, createdTimeKey} = filters

  const fitsOwner =
    opportunityIsUnallocated(opportunity) ||
    fitsOwnerFilter(user, teams, teamMembers, homeContacts, userOrTeam, showDormants, opportunity, false)

  const fitsStage = fitsLeadsStage(opportunity)
  const fitsStatus = opportunity.status === OpportunityStatus.open
  const fitsCompany = isNil(company) || opportunity.companyId === company.value
  const fitsJobType = isEmpty(jobTypes) || jobTypes.includes(opportunity.orderTypeId)
  const fitsCreated =
    fitsDateFilter(createdTimeKey, createdStart, createdEnd, invoiceFinancialYearStart, opportunity, 'createdDate')
  const fitsSearch = fitsOpportunitySearch(search, opportunity)

  return fitsStage && fitsStatus && fitsCompany && fitsJobType && fitsOwner && fitsCreated && fitsSearch
}

export const canEditStatus = (opportunity) => {
  const orders = get(opportunity, 'orders', [])
  const activeOrders = orders.filter(orderHasBeenQuoted)
  return isEmpty(activeOrders)
}

export const isWonStatusAllowed = (opportunity) => {
  return !canEditStatus(opportunity)
}

export const sortWonOpportunityList = (opportunities) => {
  return orderBy(opportunities, ['summaryInfo.confirmedDate'], ['desc'])
}

export const updateWonOpportunities = (wonOpportunities, updatedOpportunity) => {
  wonOpportunities[updatedOpportunity.ownerId] = wonOpportunities[updatedOpportunity.ownerId] || []

  return transform(wonOpportunities, (opportunitiesObject, ownerOpportunities, key) => {
    const ownerId = parseInt(key)
    opportunitiesObject[ownerId] = sortWonOpportunityList(
      updateOpportunities(ownerOpportunities, updatedOpportunity)
        .filter(opportunityIsWon)
        .filter(opportunityIsAtFinalStage)
        .filter(opportunity => opportunity.ownerId === ownerId))
  }, {})
}

export const mergeWonOpportunities = (wonOpportunities, updatedOpportunities) => {
  uniq(updatedOpportunities.map(opportunity => opportunity.ownerId)).forEach(ownerId => {
    wonOpportunities[ownerId] = wonOpportunities[ownerId] || []
  })

  return transform(wonOpportunities, (opportunitiesObject, ownerOpportunities, key) => {
    const ownerId = parseInt(key)
    opportunitiesObject[ownerId] = sortWonOpportunityList(
      mergeOpportunities(ownerOpportunities, updatedOpportunities)
        .filter(opportunityIsWon)
        .filter(opportunityIsAtFinalStage)
        .filter(opportunity => opportunity.ownerId === ownerId))
  }, {})
}

export const removeFromWonOpportunities = (wonOpportunities, opportunityId) => {
  return transform(wonOpportunities, (opportunitiesObject, ownerOpportunities, key) => {
    const ownerId = parseInt(key)
    opportunitiesObject[ownerId] = ownerOpportunities.filter(opportunity => opportunity.id !== opportunityId)
  }, {})
}

export const updateWonCounts = (wonCounts, previousOpportunity, updatedOpportunity) => {
  return updateCounts(
    wonCounts,
    get(previousOpportunity, 'ownerId'),
    get(updatedOpportunity, 'ownerId'))
}

export const updateWonValues = (wonValues, previousOpportunity, updatedOpportunity) => {
  return updateValues(
    wonValues,
    get(previousOpportunity, 'ownerId'),
    get(updatedOpportunity, 'ownerId'),
    get(previousOpportunity, 'value'),
    get(updatedOpportunity, 'value'))
}

export const getWonParams = (ownerId, filters, jobTypes, search, invoiceFinancialYearStart, page, size) => {
  const {company, wonStart, wonEnd, wonTimeKey} = filters

  const dateFilters = {
    timeKey: wonTimeKey,
    targetStart: wonStart,
    targetEnd: wonEnd,
  }

  const {targetStart, targetEnd} = getDateFilters(dateFilters, invoiceFinancialYearStart)

  return {
    ownerId,
    stageId: finalOpportunityStage,
    status: [OpportunityStatus.won],
    companyId: isNil(company) ? null : company.value,
    orderTypeId: isEmpty(jobTypes) ? null : jobTypes,
    wonStart: targetStart,
    wonEnd: targetEnd,
    search: isEmpty(search) ? null : search,
    summary: true,
    count: page === 0,
    value: page === 0,
    start: page * size,
    limit: size,
    sortColumn: ['confirmedDate'],
  }
}

export const opportunityFitsWonFilters = (
  user,
  opportunity,
  filters,
  search,
  invoiceFinancialYearStart,
  showDormants,
  teams,
  teamMembers,
  homeContacts) => {
  const {userOrTeam, jobTypes, company, wonStart, wonEnd, wonTimeKey} = filters

  const fitsOwner =
    fitsOwnerFilter(user, teams, teamMembers, homeContacts, userOrTeam, showDormants, opportunity, false)
  const fitsStage = opportunity.stageId === finalOpportunityStage
  const fitsStatus = opportunity.status === OpportunityStatus.won
  const fitsCompany = isNil(company) || opportunity.companyId === company.value
  const fitsJobType = isEmpty(jobTypes) || jobTypes.includes(opportunity.orderTypeId)
  const fitsWonDate =
    fitsDateFilter(wonTimeKey, wonStart, wonEnd, invoiceFinancialYearStart, opportunity, 'summaryInfo.confirmedDate')
  const fitsSearch = fitsOpportunitySearch(search, opportunity)

  return fitsStage && fitsStatus && fitsCompany && fitsJobType && fitsOwner && fitsWonDate && fitsSearch
}

export const getActivitiesForSummary = (activities, numberActivities) => {
  const sortedActivities = orderBy(activities, 'startDate', 'desc')
  return sortedActivities.length > numberActivities
    ? sortedActivities.slice(0, numberActivities)
    : sortedActivities
}
